import React, { useMemo, useRef } from 'react';
import { FAQ as ToolBoxFAQ } from '@navi-toolbox/ui-components';
import { useSelector } from 'react-redux';
import { LinkButton } from 'fronton-react';

import { faqWrapper } from './styles';
import { getFaqVariables } from './ducks/selectors';
import { useIntersectionObserverCallback } from '@navi-toolbox/hooks';
import { analyticsUtils } from 'utils/analytics-utils';
import { getFAQ } from 'features/page-model';

const FAQ_PARENT_LINK_DATA_QA = 'faq-parent-link';

export const FAQ = () => {
  const faqData = useSelector(getFAQ);
  const variables = useSelector(getFaqVariables);
  const ref = useRef<HTMLDivElement>(null);

  const showEventData = useMemo(
    () => ({
      elementType: 'block',
      elementBlock: 'productDetail',
      elementName: 'faq',
      elementText: faqData?.title,
    }),
    [faqData?.title],
  );

  useIntersectionObserverCallback(
    ref,
    {
      enter: () => {
        analyticsUtils.sendEvent('GA:show', showEventData);
      },
    },
    [],
  );

  const handleFaqClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const button = target.closest('[role="button"][aria-label]');
    if (!button) return;

    if (button.getAttribute('aria-expanded') !== 'false') return;

    const question = button.getAttribute('aria-label');
    if (!question) return;

    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'productDetail',
      elementName: 'faq',
      elementText: question,
    });
  };

  const variablesTemplates = useMemo(
    () => ({
      parent_link: (
        <LinkButton href={variables.parent_link} size="s" data-qa={FAQ_PARENT_LINK_DATA_QA}>
          {variables.parent_link_text.toLocaleLowerCase()}
        </LinkButton>
      ),
      date: <time dateTime={variables.date_iso}>{variables.date}</time>,
    }),
    [variables.parent_link, variables.parent_link_text, variables.date, variables.date_iso],
  );

  if (!faqData || faqData.data.length === 0) return null;

  return (
    <div className={faqWrapper} ref={ref} onClickCapture={handleFaqClickCapture}>
      <ToolBoxFAQ faq={faqData} variables={variables} variablesTemplates={variablesTemplates} />
    </div>
  );
};
